/**
 * 待办任务卡片（E01）—— 对齐后端 TodoItemVO，视觉对齐 docs/操作人员移动端 设计稿「订单卡」。
 * - 顶部：订单号（等宽，弱化）+ 状态胶囊
 * - 主行：商品名 + 数量（缺省回退订单号）
 * - 副行：数量/买家 · 柜数
 * - 航线行：船图标 起运港 —› 目的港（route 无法拆分时原样展示）
 * - 节点行：待处理/进行中 → 灰底「当前节点 · xxx」；已完成 → 蓝色节点胶囊 + 第 N 步
 * - 底部：截止/已等待（红色=待处理或逾期，灰色=其它）+ 操作按钮
 *   PENDING → 开始处理（渐变实心）；其余 → 查看详情（描边）
 * - 已完成卡片左侧 3px 绿色竖条
 */
import { Box, Typography, ButtonBase } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DirectionsBoatOutlinedIcon from '@mui/icons-material/DirectionsBoatOutlined';
import type { TodoItem, TaskStatusValue } from '@/api/types';
import { useNodeConfig } from '@/utils/nodeConfig';
import { StatusPill, cardSx } from './ui';
import { T, type Tone } from '@/theme/tokens';

interface TaskCardProps {
  task: TodoItem;
  onAction?: (task: TodoItem) => void;
}

/** 状态 → 胶囊色调 / 左竖条 */
const STATUS_META: Record<TaskStatusValue, { label: string; tone: Tone; bar?: string }> = {
  PENDING: { label: '待处理', tone: 'amber' },
  IN_PROGRESS: { label: '进行中', tone: 'blue', bar: T.blue },
  DONE: { label: '已完成', tone: 'green', bar: T.green },
  SKIPPED: { label: '已跳过', tone: 'grey', bar: T.ink4 },
  CANCELLED: { label: '已取消', tone: 'danger', bar: T.danger },
};

function fmtDue(dueAt?: string): string {
  if (!dueAt) return '';
  const d = new Date(dueAt);
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

/** 数值格式化（整数不显小数） */
function fmtNum(v?: number): string {
  if (v == null) return '';
  return String(v);
}

/** 航线拆分：「宁波→上海」「青岛 - 迪拜」「Qingdao to Dubai」→ [起, 终]；拆不开返回 null */
function splitRoute(route?: string): [string, string] | null {
  if (!route) return null;
  const parts = route.split(/\s*(?:→|->|—>|⇒|➔|～|~|至|\bto\b|\s-\s|—|–)\s*/i).filter(Boolean);
  return parts.length === 2 ? [parts[0], parts[1]] : null;
}

export default function TaskCard({ task, onAction }: TaskCardProps) {
  const { getNodeName, getNodeSeq } = useNodeConfig();
  const status = (task.taskStatus || 'PENDING') as TaskStatusValue;
  const meta = STATUS_META[status] ?? STATUS_META.PENDING;
  const overdue = Boolean(task.overdueFlag);
  const isTodo = status === 'PENDING';
  const isDone = status === 'DONE';

  // 节点名 / 序号：后端 VO 已返回，本地 nodes 配置兜底（规避 /container/nodes 偶发拉取失败）
  const nodeName = task.nodeName || getNodeName(task.nodeCode);
  const nodeSeq = task.nodeSeq ?? getNodeSeq(task.nodeCode);
  const substepName = task.substepName;

  const hasGoods = Boolean(task.productName);
  const mainLine = hasGoods
    ? `${task.productName}${task.quantity != null ? ` · ${fmtNum(task.quantity)}${task.unit ? ' ' + task.unit : ''}` : ''}`
    : (task.orderNo || task.taskId);
  const subParts = [
    task.buyerName,
    task.containerQty != null ? `${fmtNum(Math.round(task.containerQty))} 柜` : '',
  ].filter(Boolean);
  const subLine = subParts.join(' · ');
  const routePair = splitRoute(task.route);

  const dueText = task.dueAt
    ? overdue
      ? '已逾期'
      : `截止 ${fmtDue(task.dueAt)}`
    : `已等待 ${task.waitedHours != null ? task.waitedHours : '-'} 小时`;
  // 红色强调：逾期，或待处理任务的等待/截止提示；其余弱化为灰色
  const urgent = overdue || isTodo;

  return (
    <Box
      sx={{
        ...cardSx,
        p: '15px',
        mb: 1.5,
        ...(meta.bar && isDone ? { borderLeft: `3px solid ${meta.bar}` } : {}),
      }}
    >
      {/* 顶部：订单号 + 状态胶囊 */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography
          noWrap
          sx={{ fontFamily: T.mono, fontSize: '12.5px', fontWeight: 600, color: T.ink2, letterSpacing: '.01em', minWidth: 0 }}
        >
          {task.orderNo || task.taskId}
        </Typography>
        <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
      </Box>

      {/* 主行：商品 + 数量（缺省回退订单号） */}
      <Typography
        sx={{
          fontSize: hasGoods ? '16px' : '14px',
          fontWeight: 700,
          letterSpacing: '-.01em',
          color: hasGoods ? T.ink : T.brand,
          fontFamily: hasGoods ? 'inherit' : T.mono,
          mt: '10px',
          wordBreak: 'break-all',
        }}
      >
        {mainLine}
      </Typography>

      {/* 副行：买家 · 柜数 */}
      {hasGoods && subLine && (
        <Typography sx={{ fontSize: '12.5px', color: T.ink2, mt: '5px' }}>{subLine}</Typography>
      )}

      {/* 航线 */}
      {task.route && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: '11px', fontSize: '13px', fontWeight: 600, color: T.ink }}>
          <DirectionsBoatOutlinedIcon sx={{ fontSize: 17, color: T.brand }} />
          {routePair ? (
            <>
              <span>{routePair[0]}</span>
              <Box
                component="span"
                sx={{
                  flex: 1,
                  height: '1px',
                  bgcolor: T.line,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    right: 0,
                    top: '-2px',
                    width: 5,
                    height: 5,
                    borderTop: `1.5px solid ${T.ink4}`,
                    borderRight: `1.5px solid ${T.ink4}`,
                    transform: 'rotate(45deg)',
                  },
                }}
              />
              <span>{routePair[1]}</span>
            </>
          ) : (
            <span>{task.route}</span>
          )}
        </Box>
      )}

      {/* 节点 */}
      {isDone ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: '11px', flexWrap: 'wrap' }}>
          <StatusPill tone="blue">{nodeName}</StatusPill>
          {substepName && <Typography component="span" sx={{ fontSize: '11.5px', color: T.ink2 }}>/ {substepName}</Typography>}
          {nodeSeq && (
            <Typography component="span" sx={{ fontSize: '11.5px', fontWeight: 600, color: T.ink3 }}>
              第 {nodeSeq} 步
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '7px',
            mt: '11px',
            fontSize: '12px',
            color: T.ink2,
            bgcolor: T.line2,
            px: '11px',
            py: '7px',
            borderRadius: T.rSm,
            flexWrap: 'wrap',
          }}
        >
          <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: T.blue, flexShrink: 0 }} />
          <span>
            当前节点 · <Box component="b" sx={{ color: T.ink, fontWeight: 600 }}>{nodeName}</Box>
            {substepName ? ` / ${substepName}` : ''}
          </span>
          {nodeSeq && <Box component="span" sx={{ ml: 'auto', color: T.ink3 }}>第 {nodeSeq} 步</Box>}
        </Box>
      )}

      {/* 底部：时间 + 按钮 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mt: '13px',
          pt: '13px',
          borderTop: `1px solid ${T.line}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12px',
            fontWeight: 600,
            color: urgent ? T.danger : T.ink3,
            minWidth: 0,
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 15 }} />
          <span>{dueText}</span>
        </Box>
        {isTodo ? (
          <ButtonBase
            onClick={() => onAction?.(task)}
            sx={{
              flexShrink: 0,
              px: '18px',
              py: '9px',
              borderRadius: T.rPill,
              background: T.brandGrad,
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: '13.5px',
              fontWeight: 700,
              boxShadow: T.brandShadow,
              transition: 'transform .15s',
              '&:active': { transform: 'scale(.97)' },
            }}
          >
            开始处理
          </ButtonBase>
        ) : (
          <ButtonBase
            onClick={() => onAction?.(task)}
            sx={{
              flexShrink: 0,
              px: '18px',
              py: '8px',
              borderRadius: T.rPill,
              bgcolor: '#fff',
              color: T.brand,
              border: `1.5px solid ${T.brandLine}`,
              fontFamily: 'inherit',
              fontSize: '13.5px',
              fontWeight: 700,
              '&:active': { bgcolor: T.brandSoft },
            }}
          >
            查看详情
          </ButtonBase>
        )}
      </Box>
    </Box>
  );
}
